<template>
  <b-card>
    <layout-loading v-if="itemLoading" />
    <!-- form -->
    <h3>{{ $t('serviceCategories.addHeaderTitle') }}</h3>
    <b-form class="mt-2">
      <b-row>
        <b-col md="6">
          <b-form-group
            :label="$t('general.title')"
            label-for="service-category-title"
          >
            <b-form-input
              id="service-category-title"
              v-model="form.title"
              :state="validateState('title')"
              aria-describedby="input-title-feedback"
              @change="updateAltImage()"
            />
            <b-form-invalid
              id="input-title-feedback"
              :label="$t('general.title')"
              :validator="$v.form.title"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.slug')"
            label-for="service-category-slug"
          >
            <b-form-input
              id="service-category-slug"
              v-model="form.slug"
              :state="validateState('slug')"
              aria-describedby="input-slug-feedback"
            />
            <b-form-invalid
              id="input-slug-feedback"
              :label="$t('general.slug')"
              :validator="$v.form.slug"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.language')"
            label-for="service-category-language"
          >
            <languageSelector @change-selected-language="changeSelectedLanguage" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.priority')"
            label-for="services-categories-sort"
          >
            <b-form-input
              id="services-categories-sort"
              v-model.number="form.sort"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.tags')"
            label-for="service-category-add-tag"
            :state="validateState('tags')"
          >
            <v-select-custom
              id="service-category-add-tags"
              v-model="form.tags"
              :custom-props="{ multiple: true, taggable: true }"
              :options="tags"
              @search="fetchTags(form.lang, $event)"
              @option:created="addNewTag(form.lang, $event)"
            />
            <b-form-invalid
              id="input-tags-feedback"
              :label="$t('general.tags')"
              :validator="$v.form.tags"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.keywords')"
            label-for="service-categories-keywords"
          >
            <b-form-input
              id="service-categories-keywords"
              v-model="form.keywords"
              :state="validateState('keywords')"
            />
            <b-form-invalid
              id="input-keywords-feedback"
              :label="$t('general.keywords')"
              :validator="$v.form.keywords"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.description')"
            label-for="service-category-description"
          >
            <b-form-textarea
              id="service-category-description"
              v-model="form.description"
              :state="validateState('description')"
              rows="6"
              aria-describedby="input-description-feedback"
            />
            <b-form-invalid
              id="input-description-feedback"
              :label="$t('general.description')"
              :validator="$v.form.description"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="12"
          class="collapse-border"
        >
          <b-form-group
            label-for="service-category-content"
            class="mb-2"
          >
            <SunEditorBasic
              id="service-category-content"
              ref="sunEditor"
              v-model="form.content"
              :label="$t('general.content')"
              :state="validateState('content')"
            />
            <b-form-invalid
              id="input-content-feedback"
              :label="$t('general.content')"
              :validator="$v.form.content"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <div class="border rounded p-2">
            <h4 class="mb-1">
              {{ $t("general.thumbnail") }} - {{ $t("general.acceptImage") }}
            </h4>
            <b-file-input
              id="service-cat-thumbnail"
              v-model="form.serviceCatThumbnail"
              :placeholder="$t('general.select')"
              :state="validateState('serviceCatThumbnail')"
              :state-alt="validateState('serviceCatThumbnailAlt')"
              :alt.sync="form.serviceCatThumbnailAlt"
              :alt-validator="$v.form.serviceCatThumbnailAlt"
            >
              <template #invalidFeedback>
                <b-form-invalid
                  id="input-service-cat-thumbnail-feedback"
                  :is-bootstrap="false"
                  :label="$t('general.thumbnail')"
                  :validator="$v.form.serviceCatThumbnail"
                />
              </template>
            </b-file-input>
          </div>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <div class="border rounded p-2">
            <h4 class="mb-1">
              {{ $t("general.cover") }} - {{ $t("general.acceptImage") }}
            </h4>
            <b-file-input
              id="service-cat-cover-image"
              v-model="form.serviceCatCoverImage"
              :placeholder="$t('general.select')"
              :state="validateState('serviceCatCoverImage')"
              :alt.sync="form.serviceCatCoverImageAlt"
              :state-alt="validateState('serviceCatCoverImageAlt')"
              :alt-validator="$v.form.serviceCatCoverImageAlt"
            >
              <template #invalidFeedback>
                <b-form-invalid
                  id="input-service-cat-cover-image-feedback"
                  :is-bootstrap="false"
                  :label="$t('general.cover')"
                  :validator="$v.form.serviceCatCoverImage"
                />
              </template>
            </b-file-input>
          </div>
        </b-col>
        <b-col
          cols="12"
          class="mt-50"
        >
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            :disabled="isSubmit"
            @click="submitForm"
          >
            {{ isSubmit ? $t('general.isSubmit') : $t('general.submit') }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="$router.go(-1)"
          >
            {{ $t('general.back') }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
    <!--/ form -->
  </b-card>
</template>

<script>
import {
  BButton, BCard, BCol, BForm, BFormGroup, BFormInput, BFormTextarea, BRow,
} from 'bootstrap-vue'
import BFormInvalid from '@/components/BFormInvalid/index.vue'
import BFileInput from '@/components/BFileInput/index.vue'
import LayoutLoading from '@/components/LayoutLoading.vue'
import Ripple from 'vue-ripple-directive'
import VSelectCustom from '@/components/VSelectCustom/index.vue'
import languageSelector from '@/components/language-selector.vue'
import { isValidImageSize } from '@core/utils/rules'
import { required, minLength, requiredIf } from 'vuelidate/lib/validators'
import SunEditorBasic from '@/components/SunEditorBasic/index.vue'
import useServiceCategoriesList from './useServiceCategoriesList'

export default {
  components: {
    BButton,
    BCard,
    BCol,
    BFileInput,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalid,
    BFormTextarea,
    BRow,
    SunEditorBasic,
    LayoutLoading,
    VSelectCustom,
    languageSelector,
  },
  directives: {
    Ripple,
  },
  setup() {
    const {
      addItem,
      addNewTag,
      addTag,
      checkSlugIsUnique,
      createTagIdArray,
      errorManager,
      fetchTags,
      form,
      handleUpload,
      isSubmit,
      isUniqueSlug,
      itemLoading,
      newTagIds,
      tags,
    } = useServiceCategoriesList()
    return {
      addItem,
      addNewTag,
      addTag,
      checkSlugIsUnique,
      createTagIdArray,
      errorManager,
      fetchTags,
      form,
      handleUpload,
      isSubmit,
      isUniqueSlug,
      itemLoading,
      newTagIds,
      tags,
    }
  },
  validations() {
    return {
      form: {
        title: {
          required,
          minLength: minLength(3),
        },
        slug: {
          required,
          minLength: minLength(3),
          isUnique: () => this.isUniqueSlug,
        },
        description: {
          required,
          minLength: minLength(10),
        },
        content: {
          required,
          minLength: minLength(10),
        },
        tags: {
          required,
        },
        keywords: {
          required,
          minLength: minLength(3),
        },
        serviceCatCoverImage: {
          required,
          isTrueImage: requiredIf(function () {
            return typeof this.form.serviceCatCoverImage === 'object' && !Object.prototype.hasOwnProperty.call(this.form.serviceCatCoverImage, '_id')
          }),
          isValidImageSize,
        },
        serviceCatCoverImageAlt: {
          required,
          minLength: minLength(3),
        },
        serviceCatThumbnail: {
          required,
          isTrueImage: requiredIf(function () {
            return typeof this.form.serviceCatThumbnail === 'object' && !Object.prototype.hasOwnProperty.call(this.form.serviceCatThumbnail, '_id')
          }),
          isValidImageSize,
        },
        serviceCatThumbnailAlt: {
          required,
          minLength: minLength(3),
        },
      },
    }
  },
  watch: {
    'form.title': function (newValue, oldValue) {
      this.$set(this.form, 'slug', this.createSlug(this.form.title))
      if (oldValue === this.form.serviceCatCoverImageAlt) {
        this.$set(this.form, 'serviceCatCoverImageAlt', newValue)
      }
      if (oldValue === this.form.serviceCatThumbnailAlt) {
        this.$set(this.form, 'serviceCatThumbnailAlt', newValue)
      }
    },
    'form.slug': function () {
      this.checkSlugIsUnique(this.form.lang, this.form.slug)
    },
  },
  created() {
    this.form.lang = 'fa'
    this.form.tagIds = []
    this.fetchTags('fa')
  },
  methods: {
    updateAltImage() {
      if (!this.form.serviceCatCoverImageAlt) {
        this.$set(this.form, 'serviceCatCoverImageAlt', this.form.title)
      }
      if (!this.form.serviceCatThumbnailAlt) {
        this.$set(this.form, 'serviceCatThumbnailAlt', this.form.title)
      }
    },
    validateState(name) {
      const {
        $dirty,
        $error,
      } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    changeSelectedLanguage(selectedLanguage) {
      this.form.lang = selectedLanguage
      this.fetchTags(selectedLanguage)
    },
    submitForm() {
      this.$refs.sunEditor.sendValToForm()
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        this.errorManager('validator.validationError')
        setTimeout(() => document.getElementsByClassName('is-invalid')[0].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        }), 500)
        return
      }
      const uploadList = ['serviceCatCoverImage', 'serviceCatThumbnail']
      this.handleUpload(this.form, uploadList).then(newForm => {
        this.sendData(newForm)
      })
    },
    sendData(form) {
      const data = {
        title: form.title,
        slug: form.slug,
        sort: form.sort,
        keywords: form.keywords,
        description: form.description,
        content: form.content,
        tagIds: this.createTagIdArray(form.tags, this.newTagIds),
        serviceCatCoverImageFileId: form.serviceCatCoverImage ? form.serviceCatCoverImage._id : null,
        serviceCatThumbnailFileId: form.serviceCatThumbnail ? form.serviceCatThumbnail._id : null,
      }
      this.addItem(data, this.form.lang)
    },
  },
}
</script>
